import {
  Box,
  FieldLabel,
  FieldWrapper,
  FlexBox,
  Icon,
  IconNames,
  InputText,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import * as React from 'react';

import { durationConverter } from 'utils/duration';

type FieldTextProps = React.ComponentProps<typeof InputText>;
type FieldWrapperProps = React.ComponentProps<typeof FieldWrapper>;

export type FieldDurationPropsT = {
  input: {
    onChange(value: number): void;
    value: number;
  } & Omit<FieldTextProps, 'onChange' | 'value'>;
  meta?: FieldWrapperProps['meta'];
  icon?: IconNames;
  tooltip?: string;
  className?: string;
  includeDays: boolean;
  label?: string;
  noAlert?: FieldWrapperProps['noAlert'];
} & Pick<FieldWrapperProps, 'noAlert' | 'required'>;

type TimeKeyT = 'seconds' | 'minutes' | 'hours' | 'days';

type InternalValuesT = {
  hours: string;
  minutes: string;
  seconds: string;
  days?: string;
};
type StateT = {
  value: FieldDurationPropsT['input']['value'];
  internalValues: InternalValuesT;
};

function convertValueToNumber(value: string | number | null): number {
  let numberValue = value as number;
  if (typeof numberValue === 'string') {
    numberValue = parseInt(numberValue, 10) || 0;
  }

  if (numberValue === null) {
    return 0;
  }

  return numberValue;
}

function getSplitTimeUnits(value: number | string, includeDays: boolean) {
  return durationConverter.splitTimeUnits(
    convertValueToNumber(value),
    includeDays,
  );
}

function getInternalValue(seconds: string | number, includeDays: boolean) {
  const timeValues = getSplitTimeUnits(seconds, includeDays);
  const timeValuesKeys = Object.keys(timeValues);
  return timeValuesKeys.reduce<InternalValuesT>((acc, key) => {
    return {
      ...acc,
      [key]: timeValues[key].toString(),
    };
  }, {} as InternalValuesT);
}

export class FieldDuration extends React.PureComponent<
  FieldDurationPropsT,
  StateT
> {
  static defaultProps = {
    meta: {},
  };

  static getDerivedStateFromProps(props: FieldDurationPropsT, state: StateT) {
    if (props.input.value !== state.value) {
      return {
        value: props.input.value,
        internalValues: getInternalValue(props.input.value, props.includeDays),
      };
    }

    return null;
  }

  constructor(props: FieldDurationPropsT) {
    super(props);

    this.state = {
      value: props.input.value,
      internalValues: getInternalValue(props.input.value, props.includeDays),
    };
  }

  internalValueFormatter(value: string): string {
    const valueNumber = parseInt(value, 10) || 0;
    if (Number.isNaN(valueNumber) || valueNumber < 0) {
      return '0';
    }

    return value;
  }

  createInputGetter(timeKey: TimeKeyT) {
    return () => {
      const { internalValues } = this.state;
      // const value = this.getTimeValues()[timeKey];
      const internalValue = internalValues[timeKey];
      return {
        name: `${this.props.input.name || 'duration'}-${timeKey}`,
        value: internalValue ?? '',
        onBlur: this.handleChange,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          this.setState({
            internalValues: {
              ...internalValues,
              [timeKey]: this.internalValueFormatter(e.target.value),
            },
          });
        },
      };
    };
  }

  getSecondsInput = this.createInputGetter('seconds');
  getMinutesInput = this.createInputGetter('minutes');
  getHoursInput = this.createInputGetter('hours');
  getDaysInput = this.createInputGetter('days');

  combineInternalToValue(): number {
    const { internalValues } = this.state;
    return durationConverter.combineTimeUnits(
      convertValueToNumber(internalValues?.days || 0),
      convertValueToNumber(internalValues.hours || 0),
      convertValueToNumber(internalValues.minutes || 0),
      convertValueToNumber(internalValues.seconds || 0),
    );
  }

  setInternalValue(seconds: string | number, includeDays: boolean): void {
    this.setState({
      internalValues: getInternalValue(seconds, includeDays),
    });
  }

  @boundMethod
  handleChange(): void {
    const timeValues = getSplitTimeUnits(
      this.combineInternalToValue(),
      this.props.includeDays,
    );
    const seconds = durationConverter.combineTimeUnits(
      timeValues.days || 0,
      timeValues.hours,
      timeValues.minutes,
      timeValues.seconds,
    );

    this.setInternalValue(seconds, this.props.includeDays);
    this.triggerChange(Math.max(0, seconds));
  }

  triggerChange(value: number): void {
    const { input } = this.props;
    const { onChange } = input;
    onChange(value);
  }

  render() {
    const {
      icon,
      tooltip,
      includeDays,
      label,
      meta,
      noAlert,
      className,
      required,
    } = this.props;
    const boxMargin = {
      flex: '1',
      margin: '0 0 0 200',
    };
    const daysInput = this.getDaysInput();
    const hoursInput = this.getHoursInput();
    const minutesInput = this.getMinutesInput();
    const secondsInput = this.getSecondsInput();

    return (
      <FieldWrapper
        meta={meta}
        required={required}
        label={label}
        noLabel={!label}
        noAlert={noAlert}
      >
        <FlexBox
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          className={className}
        >
          {icon && (
            <Tooltip content={tooltip}>
              <Box padding="500 300 100 0">
                <Icon name={icon} size={16} />
              </Box>
            </Tooltip>
          )}

          {includeDays && (
            <Box flex="1">
              <Box margin="0 0 100">
                <FieldLabel
                  required={!label && required}
                  htmlFor={daysInput.name}
                >
                  Days(s)
                </FieldLabel>
              </Box>
              <InputText type="number" min={0} step={1} {...daysInput} />
            </Box>
          )}

          <Box {...(includeDays ? boxMargin : {})}>
            <Box margin="0 0 100">
              <FieldLabel
                required={!label && required}
                htmlFor={hoursInput.name}
              >
                Hour(s)
              </FieldLabel>
            </Box>
            <InputText type="number" min={0} step={1} {...hoursInput} />
          </Box>
          <Box {...boxMargin}>
            <Box margin="0 0 100">
              <FieldLabel
                required={!label && required}
                htmlFor={minutesInput.name}
              >
                Minute(s)
              </FieldLabel>
            </Box>
            <InputText
              type="number"
              min={0}
              max={60}
              step={1}
              {...minutesInput}
            />
          </Box>

          {!includeDays && (
            <Box {...boxMargin}>
              <Box margin="0 0 100">
                <FieldLabel
                  required={!label && required}
                  htmlFor={secondsInput.name}
                >
                  Second(s)
                </FieldLabel>
              </Box>
              <InputText type="number" min={0} step={1} {...secondsInput} />
            </Box>
          )}
        </FlexBox>
      </FieldWrapper>
    );
  }
}
